/**
 * Fuzzy subsequence matching for a short identifier list — the Dev Tools ▸ Feature Toggles filter.
 *
 * A query matches when its characters appear in order in the candidate, not necessarily adjacent, so
 * "symoutr" finds `symphonyOutreachCallTab` and "jharness" finds `specs.symphony.JavaHarness`. The
 * score ranks the matches: adjacent characters and characters that open a word (string start, after a
 * separator, or a camelCase hump) are what a dev is actually typing, so they are worth far more than a
 * character found somewhere in the middle.
 *
 * Deliberate 1:1 mirror of native `FuzzyMatch.swift` / `FuzzyMatch.kt` — same bonuses, same penalty,
 * same tie-breaking — so the same query ranks the same list identically on all three surfaces. Change
 * the three together.
 */

const ADJACENT_BONUS = 8;
const BOUNDARY_BONUS = 6;
const CHARACTER_SCORE = 1;
/** How far into the candidate a match may start before the leading-gap penalty stops growing. */
const MAX_START_PENALTY = 10;

const SEPARATORS = new Set(['.', '-', '_', ' ', '/', ':']);

/** Uppercase in the sense all three surfaces agree on — a digit or a caseless script is not. */
const isUpper = (char: string) => char !== char.toLowerCase();

function isBoundary(chars: readonly string[], index: number): boolean {
  if (index === 0) return true;
  const previous = chars[index - 1];
  if (SEPARATORS.has(previous)) return true;
  return isUpper(chars[index]) && !isUpper(previous);
}

/** Scores one anchored attempt: greedy left-to-right from `start`, or `null` if a character runs out. */
function scoreFrom(
  needle: readonly string[],
  lower: readonly string[],
  chars: readonly string[],
  start: number,
): number | null {
  let score = 0;
  let cursor = start;
  let previousMatch = -1;

  for (const char of needle) {
    while (cursor < lower.length && lower[cursor] !== char) cursor += 1;
    if (cursor >= lower.length) return null;

    score += CHARACTER_SCORE;
    if (previousMatch >= 0 && cursor === previousMatch + 1) score += ADJACENT_BONUS;
    if (isBoundary(chars, cursor)) score += BOUNDARY_BONUS;

    previousMatch = cursor;
    cursor += 1;
  }

  return score - Math.min(start, MAX_START_PENALTY);
}

/**
 * How well `query` matches `text`, or `null` when it doesn't match at all. An empty query matches
 * everything with score 0, so a blank search box leaves the list in its original order.
 *
 * Anchored at every position the first character could start from, keeping the best — a plain greedy
 * scan would match "sym" against the leading `s` of `specs.symphony` and miss the run that the dev
 * meant.
 */
export function fuzzyScore(query: string, text: string): number | null {
  // Per-character folding rather than lowercasing the whole string: a locale-specific fold can change
  // a string's length, and the boundary check indexes the ORIGINAL characters.
  const needle = Array.from(query.trim(), (char) => char.toLowerCase());
  if (needle.length === 0) return 0;

  const chars = Array.from(text);
  const lower = chars.map((char) => char.toLowerCase());
  if (needle.length > lower.length) return null;

  let best: number | null = null;
  for (let start = 0; start <= lower.length - needle.length; start += 1) {
    if (lower[start] !== needle[0]) continue;
    const candidate = scoreFrom(needle, lower, chars, start);
    if (candidate !== null && (best === null || candidate > best)) best = candidate;
  }
  return best;
}

/**
 * The matching items, best first. Ties keep the caller's order, so an empty query is the identity and
 * equally-good matches stay in the order the list was declared in.
 */
export function fuzzyFilter<T>(items: readonly T[], query: string, textOf: (item: T) => string): T[] {
  return items
    .map((item, index) => ({ item, index, score: fuzzyScore(query, textOf(item)) }))
    .filter((row): row is { item: T; index: number; score: number } => row.score !== null)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((row) => row.item);
}
